import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import { isStoragePath } from "@/lib/mediaStorage";

interface SeoSnippetPreviewProps {
  title: string;
  description: string;
  path: string;
  /** Social share image; when set the card shows how the share preview looks. */
  image?: string;
  /** When set, shows which real values the tokens resolved to. */
  tokens?: { title?: string; description?: string };
}

/**
 * A Google-style result card used to preview how a section template resolves
 * against a single item's real data. When a share image is present it also
 * shows the thumbnail that social cards will render.
 */
export default function SeoSnippetPreview({
  title,
  description,
  path,
  image,
  tokens,
}: SeoSnippetPreviewProps) {
  const urlMap = useAssetUrlMap([image]);
  const displayImage = image ? (isStoragePath(image) ? urlMap[image] || "" : image) : "";

  return (
    <div className="border border-background-200 rounded-md bg-background-50 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2.5 flex items-center gap-1.5">
        <i className="ri-search-eye-line text-secondary-700"></i> Live snippet preview
      </p>

      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-foreground-600 truncate">
            <i className="ri-global-line"></i> {path}
          </p>
          <p className="text-base text-primary-700 truncate mt-0.5">
            {title || "Page title"}
          </p>
          <p className="text-xs text-foreground-700 mt-1 line-clamp-2">
            {description || "Meta description preview…"}
          </p>
        </div>
        {displayImage ? (
          <div className="w-20 h-20 rounded-md overflow-hidden border border-background-300 bg-background-100 shrink-0">
            <img
              src={displayImage}
              alt="Social share card preview"
              className="w-full h-full object-cover"
            />
          </div>
        ) : null}
      </div>

      {tokens ? (
        <p className="text-[11px] text-foreground-500 mt-3 pt-3 border-t border-background-200">
          Resolved for this item —{" "}
          <span className="font-semibold text-foreground-700">
            {"{title}"}
          </span>{" "}
          → “{tokens.title || "—"}”
          {tokens.description ? (
            <>
              {", "}
              <span className="font-semibold text-foreground-700">
                {"{description}"}
              </span>{" "}
              → “{tokens.description}”
            </>
          ) : null}
        </p>
      ) : null}

      {displayImage ? (
        <p className="text-[11px] text-foreground-500 mt-3 pt-3 border-t border-background-200 flex items-center gap-1.5">
          <i className="ri-share-forward-line text-secondary-700"></i>
          This image becomes the social share card for this page.
        </p>
      ) : null}
    </div>
  );
}